import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@monorepo/shared-auth';
import { IconTrophy, IconCalendar, IconUsers, IconMessage, IconUserPlus, IconArrowLeft, IconSettings, IconPhoto } from '@tabler/icons-react';
import {
  getLeague,
  getLeagueMembers,
  isLeagueAdmin,
  submitJoinRequest,
  hasPendingRequest
} from '../../services/leagueService';
import { SPORTS_LIST } from '../../data/leagueSports';
import OverviewTab from '../../components/league/OverviewTab';
import MembersTab from '../../components/league/MembersTab';
import MatchesTab from '../../components/league/MatchesTab';
import StandingsTab from '../../components/league/StandingsTab';
import MessagesTab from '../../components/league/MessagesTab';
import GalleryTab from '../../components/league/GalleryTab';
import JoinRequestsTab from '../../components/league/JoinRequestsTab';
import LeagueSettingsModal from '../../components/league/LeagueSettingsModal';
import TermsModal from '../../components/league/Legal/TermsModal';

const LeagueDetail = () => {
  const { leagueId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [members, setMembers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [showJoinTerms, setShowJoinTerms] = useState(false);

  useEffect(() => {
    loadLeagueData();
  }, [leagueId, currentUser]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      const [leagueData, membersData, adminStatus, pendingStatus] = await Promise.all([
        getLeague(leagueId),
        getLeagueMembers(leagueId),
        currentUser ? isLeagueAdmin(leagueId, currentUser.uid) : Promise.resolve(false),
        currentUser ? hasPendingRequest(leagueId, currentUser.uid) : Promise.resolve(false)
      ]);

      setLeague(leagueData);
      setMembers(membersData);
      setIsAdmin(adminStatus);
      setHasPending(pendingStatus);

      // Check if current user is a member
      const userIsMember = currentUser && membersData.some(m => m.userId === currentUser.uid);
      setIsMember(userIsMember);
    } catch (error) {
      console.error('Error loading league:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToJoinClick = () => {
    if (!currentUser) {
      alert('You must be logged in to request to join a league.');
      return;
    }
    // Show terms modal first
    setShowJoinTerms(true);
  };

  const handleAcceptTermsAndJoin = async () => {
    setShowJoinTerms(false);
    setRequesting(true);
    try {
      await submitJoinRequest(leagueId, currentUser.uid, {
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        email: currentUser.email || '',
        termsAcceptedAt: new Date().toISOString()
      });
      setHasPending(true);
      alert('Your request to join has been submitted!');
    } catch (error) {
      console.error('Error submitting join request:', error);
      alert('Failed to submit join request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="ub-league-detail-page">
        <div className="ub-loading-container">
          <div className="ub-loading-spinner"></div>
          <p>Loading league...</p>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="ub-league-detail-page">
        <div className="ub-not-found">
          <h2>League Not Found</h2>
          <p>This league doesn't exist or has been removed.</p>
          <Link to="/leagues" className="ub-btn-back">
            Back to Leagues
          </Link>
        </div>
      </div>
    );
  }

  const sport = SPORTS_LIST.find(s => s.id === league.sport);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: IconTrophy },
    { id: 'matches', label: 'Matches', icon: IconCalendar },
    { id: 'standings', label: 'Standings', icon: IconTrophy },
    { id: 'members', label: 'Members', icon: IconUsers },
    { id: 'messages', label: 'Messages', icon: IconMessage },
    { id: 'gallery', label: 'Gallery', icon: IconPhoto },
    ...(isAdmin ? [{ id: 'requests', label: 'Join Requests', icon: IconUserPlus, adminOnly: true }] : [])
  ];

  return (
    <div className="ub-league-detail-page">
      <div className="ub-league-detail-container">
        <div className="ub-league-header">
          <button onClick={() => navigate('/leagues')} className="ub-btn-back-header">
            <IconArrowLeft size={20} />
            <span>Back to Leagues</span>
          </button>

          <div className="ub-league-title-section">
            <div className="ub-league-sport-badge">
              <IconTrophy size={32} stroke={1.5} />
            </div>
            <div className="ub-league-title-info">
              <h1>{league.name}</h1>
              <div className="ub-league-meta-info">
                <span className="ub-meta-sport">{sport?.name || league.sport}</span>
                <span className="ub-meta-separator">•</span>
                <span className="ub-meta-format">
                  {league.format?.replace('-', ' ')}
                </span>
                <span className="ub-meta-separator">•</span>
                <span className="ub-meta-members">{members.length} members</span>
              </div>
            </div>
          </div>

          <div className="ub-header-actions">
            {isAdmin && (
              <button className="ub-btn-settings" onClick={() => setShowSettings(true)}>
                <IconSettings size={20} />
                <span>Settings</span>
              </button>
            )}
            {!isMember && !isAdmin && currentUser && !league?.isFilled && (
              <button
                className="ub-btn-request-join"
                onClick={handleRequestToJoinClick}
                disabled={requesting || hasPending}
              >
                <IconUserPlus size={20} />
                <span>{hasPending ? 'Request Pending' : 'Request to Join'}</span>
              </button>
            )}
            {!isMember && !isAdmin && currentUser && league?.isFilled && (
              <div className="ub-league-filled-badge">
                League Full
              </div>
            )}
          </div>
        </div>

        <div className="ub-league-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`ub-league-tab ${activeTab === tab.id ? 'ub-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="ub-league-tab-content">
          {activeTab === 'overview' && (
            <OverviewTab
              league={league}
              members={members}
              isAdmin={isAdmin}
              onUpdate={loadLeagueData}
            />
          )}
          {activeTab === 'matches' && (
            <MatchesTab
              leagueId={leagueId}
              league={league}
              members={members}
              isAdmin={isAdmin}
            />
          )}
          {activeTab === 'standings' && (
            <StandingsTab
              leagueId={leagueId}
              league={league}
              members={members}
              isAdmin={isAdmin}
            />
          )}
          {activeTab === 'members' && (
            <MembersTab
              leagueId={leagueId}
              league={league}
              members={members}
              isAdmin={isAdmin}
              onUpdate={loadLeagueData}
            />
          )}
          {activeTab === 'messages' && (
            <MessagesTab
              leagueId={leagueId}
              currentUser={currentUser}
            />
          )}
          {activeTab === 'gallery' && (
            <GalleryTab
              leagueId={leagueId}
              currentUser={currentUser}
              isAdmin={isAdmin}
              isMember={isMember}
            />
          )}
          {activeTab === 'requests' && isAdmin && (
            <JoinRequestsTab
              leagueId={leagueId}
              onUpdate={loadLeagueData}
            />
          )}
        </div>
      </div>

      {showSettings && (
        <LeagueSettingsModal
          league={league}
          onClose={() => setShowSettings(false)}
          onUpdate={loadLeagueData}
        />
      )}

      {showJoinTerms && (
        <TermsModal
          onClose={() => setShowJoinTerms(false)}
          showAcceptButton={true}
          onAccept={handleAcceptTermsAndJoin}
        />
      )}
    </div>
  );
};

export default LeagueDetail;
